import {
  AnchorButton,
  Drawer,
  Intent,
  NonIdealState,
  Position,
} from '@blueprintjs/core';
import * as React from 'react';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectRuleOverviewDrawerView } from 'src/containers/ProjectRuleOverviewDrawer/ProjectRuleOverviewDrawerView';
import { projectRulesByIdSelector } from 'src/selectors/projectRules';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ProjectRuleOverviewDrawerEmpty: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const handleCreateRule = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: true }));
  }, [dispatch]);

  return (
    <NonIdealState
      action={
        <AnchorButton
          data-testid={TEST_IDS.ruleOverviewDrawer.emptyCreateButton}
          intent={Intent.PRIMARY}
          onClick={handleCreateRule}
          text={copy.components.projectRuleOverviewDrawer.createRule}
        />
      }
      description={
        copy.components.projectRuleOverviewDrawer.emptyState.description
      }
      title={copy.components.projectRuleOverviewDrawer.emptyState.title}
    />
  );
};

const ProjectRuleOverviewDrawerBody: React.VFC = () => {
  const { projectId } = useRouterParams();
  const rulesById = useReduxSelector(projectRulesByIdSelector(projectId));
  const hasRules = Boolean(Object.keys(rulesById).length);

  if (!hasRules) {
    return <ProjectRuleOverviewDrawerEmpty />;
  }

  return <ProjectRuleOverviewDrawerView />;
};

export const ProjectRuleOverviewDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { isOpen } = useReduxSelector((state) => state.ui.ruleOverviewDrawer);

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledRuleOverviewDrawer({ isOpen: false }));
  }, [dispatch]);

  const handleCreateRule = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: true }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      className="sd-drawer-project-rule-overview"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={
        <div
          className="sd-drawer-project-rule-overview__header"
          data-testid={TEST_IDS.ruleOverviewDrawer.header}
        >
          <div>{copy.components.projectRuleOverviewDrawer.title}</div>

          <AnchorButton
            data-testid={TEST_IDS.ruleOverviewDrawer.createButton}
            intent={Intent.PRIMARY}
            minimal
            onClick={handleCreateRule}
            outlined
            text={copy.components.projectRuleOverviewDrawer.createRule}
          />
        </div>
      }
      usePortal={false}
    >
      <ProjectRuleOverviewDrawerBody />
    </Drawer>
  );
};
